import { Box, SxProps, Theme, useTheme } from '@mui/material';
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { KeyboardKeyEnum } from '../../../types/shell';

interface ITabItem<T> {
  label: string;
  value: T;
  Icon?: React.ElementType;
  dataAction?: string;
}

interface IProps<T extends string = string> {
  tabs: ITabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  sx?: SxProps<Theme>;
}

export const ToggleSwitch = <T extends string>({ tabs, value, onChange, sx }: IProps<T>) => {
  const theme = useTheme();

  const listRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const activeIndex = useMemo(() => tabs.findIndex((tab) => tab.value === value), [tabs, value]);

  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const handleSelect = useCallback(
    (tabValue: T) => {
      if (tabValue !== value) {
        onChange(tabValue);
      }
    },
    [onChange, value],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== KeyboardKeyEnum.ArrowLeft && event.key !== KeyboardKeyEnum.ArrowRight) {
        return;
      }
      event.preventDefault();
      const direction = event.key === KeyboardKeyEnum.ArrowRight ? 1 : -1;
      const nextIndex = (activeIndex + direction + tabs.length) % tabs.length;
      const nextTab = tabs[nextIndex];
      if (nextTab) {
        handleSelect(nextTab.value);
        tabRefs.current[nextTab.value]?.focus();
      }
    },
    [activeIndex, tabs, handleSelect],
  );

  useLayoutEffect(() => {
    setFocusedIndex(null);
  }, [tabs.length]);

  return (
    <Box
      ref={listRef}
      role="tablist"
      aria-label="Address source"
      onKeyDown={handleKeyDown}
      sx={{
        position: 'relative',
        height: 40,
        borderRadius: 1,
        backgroundColor: 'grey.200',
        boxShadow: 'inset 0px 0px 2px 0px rgba(0,0,0,0.1)',
        p: '5px',
        display: 'flex',
        alignItems: 'stretch',
        ...sx,
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '5px',
          left: '5px',
          width: `calc(${100 / tabs.length}% - 5px)`,
          height: 30,
          borderRadius: 1,
          backgroundColor: 'common.white',
          boxShadow: '0px 1px 5px 0px rgba(0,0,0,0.12)',
          transition: 'transform 200ms cubic-bezier(0.4,0,0.2,1)',
          transform: `translateX(${activeIndex * 100}%)`,
        }}
      />
      {tabs.map((tab, index) => {
        const { value: tabValue, label: tabLabel, Icon: TabIcon, dataAction } = tab;
        const isSelected = tabValue === value;

        return (
          <Box
            key={tabValue}
            component="button"
            type="button"
            role="tab"
            aria-selected={isSelected}
            tabIndex={isSelected ? 0 : -1}
            data-action={dataAction}
            ref={(el: HTMLButtonElement | null) => (tabRefs.current[tabValue] = el)}
            onClick={() => handleSelect(tabValue)}
            onFocus={() => setFocusedIndex(index)}
            onBlur={() => setFocusedIndex(null)}
            sx={{
              position: 'relative',
              zIndex: 1,
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 500,
              lineHeight: '22px',
              whiteSpace: 'nowrap',
              padding: 0,
              color: isSelected ? 'text.primary' : 'grey.600',
              transition: 'color 200ms cubic-bezier(0.4,0,0.2,1)',
              outline: focusedIndex === index ? `2px solid ${theme.palette.primary.main}` : 'none',
              outlineOffset: 2,
            }}
          >
            {TabIcon && (
              <TabIcon
                sx={{
                  width: 18,
                  height: 18,
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              />
            )}
            {tabLabel}
          </Box>
        );
      })}
    </Box>
  );
};
